// Service pour la gestion des catalogues fournisseurs
import { apiFetch } from './httpClient';

export interface Fournisseur {
  nom: string;
  nb_produits: number;
}

export interface UserFournisseur {
  id: number;
  nom: string;
  statut: string;
  remise_globale: number;
  date_affiliation: string | null;
  conditions_paiement: string | null;
  notes: string | null;
  nb_produits: number;
}

export interface ProduitCatalogue {
  id: number;
  reference: string;
  nom_commercial: string;
  categorie_principale: string;
  sous_categorie: string;
  type_produit: string;
  prix_public_ht: number;
  unite_prix: string;
  composition?: string;
  laize?: number;
  poids?: number;
  epaisseur?: number;
  description_technique?: string;
  couleurs_disponibles?: any;
  image_url?: string;
}

export interface CataloguePagination {
  produits: ProduitCatalogue[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

class CatalogueFournisseurService {
  // Liste tous les fournisseurs disponibles
  async getFournisseursDisponibles(): Promise<Fournisseur[]> {
    return apiFetch('/catalogues/fournisseurs');
  }

  // Liste les fournisseurs d'un utilisateur
  async getUserFournisseurs(userId: number): Promise<UserFournisseur[]> {
    return apiFetch(`/users/${userId}/fournisseurs`);
  }

  // Ajoute un fournisseur à un utilisateur
  async addUserFournisseur(
    userId: number,
    data: {
      fournisseur: string;
      remise_globale?: number;
      conditions_paiement?: string;
      notes?: string;
    }
  ): Promise<{ message: string; id: number }> {
    return apiFetch(`/users/${userId}/fournisseurs`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Met à jour un fournisseur utilisateur
  async updateUserFournisseur(
    userId: number,
    fournisseurId: number,
    data: Partial<{
      remise_globale: number;
      conditions_paiement: string;
      notes: string;
      statut: string;
    }>
  ): Promise<{ message: string }> {
    return apiFetch(`/users/${userId}/fournisseurs/${fournisseurId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Supprime un fournisseur utilisateur
  async deleteUserFournisseur(
    userId: number,
    fournisseurId: number
  ): Promise<{ message: string }> {
    return apiFetch(`/users/${userId}/fournisseurs/${fournisseurId}`, {
      method: 'DELETE',
    });
  }

  // Récupère les produits d'un catalogue fournisseur
  async getCatalogueProduits(
    fournisseur: string,
    filters?: {
      categorie?: string;
      sous_categorie?: string;
      type_produit?: string;
      search?: string;
      page?: number;
      per_page?: number;
    }
  ): Promise<CataloguePagination> {
    const params = new URLSearchParams();
    if (filters?.categorie) params.append('categorie', filters.categorie);
    if (filters?.sous_categorie) params.append('sous_categorie', filters.sous_categorie);
    if (filters?.type_produit) params.append('type_produit', filters.type_produit);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());

    return apiFetch(
      `/catalogues/${encodeURIComponent(fournisseur)}/produits?${params.toString()}`
    );
  }

  // Récupère les catégories disponibles
  async getCategories(fournisseur?: string): Promise<{
    categories: string[];
    sous_categories: { [key: string]: string[] };
    types_produits: { [key: string]: string[] };
  }> {
    const params = fournisseur ? `?fournisseur=${encodeURIComponent(fournisseur)}` : '';
    return apiFetch(`/catalogues/categories${params}`);
  }

  // Importe un produit dans le catalogue personnel
  async importerProduit(data: {
    user_id: number;
    tenant_id: number;
    produit_id: number;
    remise_appliquee?: number;
    personnalisation?: {
      nom?: string;
      prix?: number;
    };
  }): Promise<{
    message: string;
    produit_id: number;
    table: string;
    prix_applique: number;
  }> {
    return apiFetch(`/catalogues/importer`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Vérifie si un produit existe déjà
  async verifierDoublon(
    userId: number,
    tenantId: number,
    nom: string,
    categorie: string
  ): Promise<{ existe: boolean; produit?: any }> {
    return apiFetch(`/catalogues/verifier-doublon`, {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        tenant_id: tenantId,
        nom,
        categorie,
      }),
    });
  }
}

export default new CatalogueFournisseurService();
